/**
 * Topic cover image resolution - single source of truth.
 *
 * Priority chain:
 *   1. topic.coverImageUrl from the stable same-origin R2 delivery route
 *   2. Category cover: cover-{bestCategorySlug}.webp
 *   3. Other cover: cover-other.webp (the universal fallback)
 *
 * When a topic belongs to multiple categories, the **narrowest** category
 * wins (e.g. a topic tagged Tech + Marketing uses the Marketing cover,
 * because Tech is too broad to be visually distinctive).
 *
 * Topics with no classification at all fall back to the "other" cover,
 * which is the broadest catch-all category. There is no separate
 * "default" cover image - "other" IS the default.
 *
 * To change the priority order, edit CATEGORY_PRIORITY below - every
 * render site picks up the change automatically.
 */

/** Category slug -> priority rank (1 = narrowest/highest priority). */
const CATEGORY_PRIORITY: Record<string, number> = {
	'legal-compliance': 1,
	'health-longevity': 2,
	'finance-investing': 3,
	'marketing-growth': 4,
	'entrepreneurship': 5,
	'career-professional': 6,
	'personal-development': 7,
	'education-learning': 8,
	'technology-ai': 9,
	other: 10,
};

/**
 * Universal fallback cover - the "other" category image.
 * Topics with no classification resolve to this.
 */
export const DEFAULT_COVER_URL = categoryCoverUrl('other');

/** Category slug -> cover asset path. */
function categoryCoverUrl(slug: string): string {
	return `/assets/covers/cover-${slug}.webp`;
}

/** Minimal input shape — any object with a slug + label works. */
interface CategoryLike {
	slug: string;
	label?: string;
}

/**
 * Pick the best category cover for a topic based on its categories.
 * Returns the narrowest (most specific) category's cover image URL,
 * or the default cover if the topic has no known categories.
 *
 * @param categories All categories the topic belongs to (primary + secondary).
 *                   Pass an empty array or null to get the default cover.
 */
export function coverUrlForTopic(categories: CategoryLike[] | null | undefined): string {
	if (!categories || categories.length === 0) return DEFAULT_COVER_URL;

	let bestSlug: string | null = null;
	let bestRank = Infinity;
	for (const cat of categories) {
		const rank = CATEGORY_PRIORITY[cat.slug];
		if (rank !== undefined && rank < bestRank) {
			bestRank = rank;
			bestSlug = cat.slug;
		}
	}

	return bestSlug ? categoryCoverUrl(bestSlug) : DEFAULT_COVER_URL;
}

/**
 * Flatten a TopicClassification into a simple category list for coverUrlForTopic.
 */
export function categoriesFromClassification(classification: {
	primaryCategory: { slug: string; label: string } | null;
	secondaryCategories: { slug: string; label: string }[];
} | null | undefined): CategoryLike[] {
	if (!classification) return [];
	const primary = classification.primaryCategory ? [classification.primaryCategory] : [];
	return [...primary, ...classification.secondaryCategories];
}

/**
 * The database key never crosses the page-model boundary. A present key only
 * selects the stable Worker image route; article and media authorization stay
 * on their separate protected routes.
 *
 * Generated covers use one fixed business key and are overwritten in place.
 * Keep the delivery URL stable; browsers retain a one-hour fresh response and
 * use the route ETag for a later conditional request.
 */
export function coverUrlForTopicAsset(
	topicId: string,
	coverImageR2Key: string | null | undefined,
	categories: CategoryLike[] | null | undefined,
): string {
	if (!coverImageR2Key) return coverUrlForTopic(categories);
	return `/topic-covers/${encodeURIComponent(topicId)}/cover.webp`;
}
